import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Pause, Play, RotateCcw, RotateCw } from "lucide-react";
import styles from "./styles.stylesheet.module.scss";

const formatTimelineDate = (date: Date) => {
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatTimelineTime = (date: Date) => {
  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
};

const clampPosition = (value: number, duration: number) => {
  return Math.min(Math.max(value, 0), duration || 0);
};

const SkipIcon = ({ direction }: { direction: "back" | "forward" }) => (
  <span className={styles.skipIcon} aria-hidden="true">
    {direction === "back" ? (
      <RotateCcw size={21} strokeWidth={2.2} />
    ) : (
      <RotateCw size={21} strokeWidth={2.2} />
    )}
    <span>15</span>
  </span>
);

interface AudioReplayPlayerProps {
  durationSeconds: number;
  onPositionChange?: (position: number) => void;
  src?: string;
  startedAt: string;
}

export interface AudioReplayPlayerHandle {
  seekTo: (position: number) => void;
}

export const AudioReplayPlayer = forwardRef<
  AudioReplayPlayerHandle,
  AudioReplayPlayerProps
>(function AudioReplayPlayer(
  { durationSeconds, onPositionChange, src, startedAt },
  ref,
) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const pendingSeekRef = useRef<{ requestedAt: number; target: number } | null>(
    null,
  );
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSeconds);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [seekPosition, setSeekPosition] = useState(0);

  const effectiveDuration = duration || durationSeconds || 0;
  const displayedTime = isSeeking ? seekPosition : currentTime;
  const progress = effectiveDuration
    ? Math.min((displayedTime / effectiveDuration) * 100, 100)
    : 0;

  const playbackDate = useMemo(() => {
    return new Date(new Date(startedAt).getTime() + displayedTime * 1000);
  }, [displayedTime, startedAt]);

  const endDate = useMemo(() => {
    return new Date(new Date(startedAt).getTime() + effectiveDuration * 1000);
  }, [effectiveDuration, startedAt]);

  const seekToPosition = useCallback((nextPosition: number) => {
    const audio = audioRef.current;
    const clampedPosition = clampPosition(nextPosition, effectiveDuration);

    setSeekPosition(clampedPosition);
    setCurrentTime(clampedPosition);
    onPositionChange?.(clampedPosition);
    pendingSeekRef.current = {
      requestedAt: Date.now(),
      target: clampedPosition,
    };

    if (audio) {
      audio.currentTime = clampedPosition;
    }
  }, [effectiveDuration, onPositionChange]);

  useImperativeHandle(
    ref,
    () => ({
      seekTo: seekToPosition,
    }),
    [seekToPosition],
  );

  useEffect(() => {
    setCurrentTime(0);
    setSeekPosition(0);
    setDuration(durationSeconds);
    setIsSeeking(false);
    setIsPlaying(false);
    onPositionChange?.(0);
    pendingSeekRef.current = null;
  }, [durationSeconds, onPositionChange, src]);

  const handlePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio || !src) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    await audio.play();
    setIsPlaying(true);
  };

  const seekBy = (deltaSeconds: number) => {
    if (!effectiveDuration) return;

    seekToPosition(displayedTime + deltaSeconds);
  };

  const handleSeekPreview = (value: string) => {
    const nextTime = Number(value);
    if (Number.isNaN(nextTime)) return;

    setIsSeeking(true);
    setSeekPosition(clampPosition(nextTime, effectiveDuration));
  };

  const handleSeekComplete = () => {
    if (!isSeeking) return;

    seekToPosition(seekPosition);
    setIsSeeking(false);
  };

  const handleLoadedMetadata = () => {
    const nextDuration = audioRef.current?.duration;
    if (nextDuration && Number.isFinite(nextDuration)) {
      setDuration(nextDuration);
    }
  };

  const handleTimeUpdate = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    if (isSeeking) return;

    const nextTime = event.currentTarget.currentTime;
    const pendingSeek = pendingSeekRef.current;
    if (pendingSeek) {
      const seekProgressSettleSeconds = 1.25;
      const seekProgressSettleMs = 1800;
      const isSettled =
        Math.abs(nextTime - pendingSeek.target) <= seekProgressSettleSeconds;
      const isExpired =
        Date.now() - pendingSeek.requestedAt > seekProgressSettleMs;

      if (!isSettled && !isExpired) {
        return;
      }

      pendingSeekRef.current = null;
    }

    setCurrentTime(nextTime);
    onPositionChange?.(nextTime);
  };

  return (
    <section className={styles.floatingPlayer} aria-label="Memory playback">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onEnded={() => setIsPlaying(false)}
        onLoadedMetadata={handleLoadedMetadata}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onTimeUpdate={handleTimeUpdate}
      />

      {src ? (
        <>
          <div className={styles.playerTimeRow}>
            <span>{formatTimelineTime(playbackDate)}</span>
            <span>{formatTimelineDate(playbackDate)}</span>
            <span>{formatTimelineTime(endDate)}</span>
          </div>
          <input
            className={styles.seekSlider}
            type="range"
            min={0}
            max={effectiveDuration || 1}
            step="0.1"
            value={clampPosition(displayedTime, effectiveDuration || 1)}
            onPointerDown={() => {
              setIsSeeking(true);
              setSeekPosition(displayedTime);
            }}
            onPointerUp={handleSeekComplete}
            onKeyUp={handleSeekComplete}
            onBlur={handleSeekComplete}
            onChange={(event) => handleSeekPreview(event.target.value)}
            disabled={!effectiveDuration}
            aria-label="Playback position"
            style={
              {
                "--progress": `${progress}%`,
              } as React.CSSProperties
            }
          />
          <div className={styles.controlsRow}>
            <button
              className={styles.secondaryControl}
              type="button"
              title="Back 15 seconds"
              aria-label="Back 15 seconds"
              onClick={() => seekBy(-15)}
            >
              <SkipIcon direction="back" />
            </button>
            <button
              className={styles.primaryControl}
              type="button"
              title={isPlaying ? "Pause" : "Play"}
              aria-label={isPlaying ? "Pause" : "Play"}
              onClick={handlePlayPause}
            >
              {isPlaying ? (
                <Pause size={21} strokeWidth={2.8} fill="currentColor" />
              ) : (
                <Play size={20} strokeWidth={1.8} fill="currentColor" />
              )}
            </button>
            <button
              className={styles.secondaryControl}
              type="button"
              title="Forward 15 seconds"
              aria-label="Forward 15 seconds"
              onClick={() => seekBy(15)}
            >
              <SkipIcon direction="forward" />
            </button>
          </div>
        </>
      ) : (
        <div className={styles.missingPlaybackPanel}>
          <strong>Playback unavailable</strong>
          <span>This memory does not have a playable audio file yet.</span>
        </div>
      )}
    </section>
  );
});
